/*
 * Settings → Operations → Pressure Control (fieldiq-api#1268, BOP-1).
 *
 * The tenant's certificate limits — GET / PUT /pressure-control/settings:
 *   - years in service before recertification (default 3),
 *   - total years from certification for a component with elastomers (default 5),
 *   - days of warning before the last valid day (default 60).
 *
 * They are DEFAULTS the tenant may edit (Decision Q6), not a regulation, and a
 * change applies to certificates recorded AFTER it: each certificate keeps the
 * limits it was recorded under. The page says both.
 *
 * Cross-vertical, like Equipment: no vertical gate on the tab. Writes are
 * admin and office staff (plus a dispatcher with admin access); everyone else
 * reads with the controls disabled. A tenant with no row reads the API's
 * defaults with `stored: false`, and the page says so. Only changed fields are
 * sent.
 *
 * BOP-2 (api#1281) adds two cards below, each with its own Save: the crew-drill
 * interval and the default test pressures (PcRecordSettingsCards).
 *
 * WL-0 (api#1333) adds Certificate rules by class (PcClassRulesCard) directly
 * under the company limits it falls back to.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { usePcSettings, useUpdatePcSettings } from '../../hooks/usePressureControl';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { PcDrillSettingsCard, PcTestDefaultsCard } from '../../components/pressure-control/PcRecordSettingsCards';
import { PcClassRulesCard } from '../../components/pressure-control/PcClassRulesCard';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminPc, pcErrorMessage } from '../../lib/pressure-control';
import type { PcSettings, PcSettingsWrite } from '../../types/pressure-control';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';

const FIELDS = [
  { key: 'max_in_service_years', read: 'maxInServiceYears', label: 'Years in service', min: 1, max: 20,
    hint: 'From the day a certified component goes into service to its last valid day.' },
  { key: 'max_total_years', read: 'maxTotalYears', label: 'Total years with elastomers', min: 1, max: 20,
    hint: 'From the certificate day, for a component with elastomers, whether or not it is in service.' },
  { key: 'warn_days', read: 'warnDays', label: 'Warning days', min: 0, max: 365,
    hint: 'A component reads Due soon this many days before its last valid day.' },
] as const;

type Key = (typeof FIELDS)[number]['key'];

const textsOf = (s: PcSettings): Record<Key, string> => ({
  max_in_service_years: String(s.maxInServiceYears),
  max_total_years: String(s.maxTotalYears),
  warn_days: String(s.warnDays),
});

/** The PUT body for what changed, or an error sentence. */
export function pcSettingsBody(before: PcSettings, texts: Record<Key, string>): PcSettingsWrite | string {
  const body: PcSettingsWrite = {};
  const values: Record<string, number> = {};
  for (const f of FIELDS) {
    const raw = texts[f.key].trim();
    if (!/^\d+$/.test(raw) || Number(raw) < f.min || Number(raw) > f.max) {
      return `${f.label} must be a whole number from ${f.min} to ${f.max}.`;
    }
    values[f.key] = Number(raw);
    if (values[f.key] !== (before as any)[f.read]) (body as any)[f.key] = values[f.key];
  }
  if (values.max_in_service_years > values.max_total_years) {
    return 'Years in service cannot be more than total years.';
  }
  return body;
}

export default function PressureControlSettingsPage() {
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canWrite = canAdminPc(user?.role, elevated);
  const query = usePcSettings();
  const save = useUpdatePcSettings();
  const failure = queryLoadFailure(query);
  const settings = query.data ?? null;
  const [texts, setTexts] = useState<Record<Key, string> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed once; a background refetch must not wipe what the reader is typing.
  useEffect(() => {
    if (settings && texts === null) setTexts(textsOf(settings));
  }, [settings, texts]);

  if (failure) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice
          error={queryFailureError(query)}
          kind={failure}
          subject="pressure-control settings"
          allowed="admins, office staff, supervisors, dispatchers, field workers and logistics"
        />
      </div>
    );
  }

  if (query.isLoading || !settings || !texts) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading pressure-control settings" />
      </div>
    );
  }

  const body = pcSettingsBody(settings, texts);
  const dirty = typeof body === 'string' || Object.keys(body).length > 0;

  const onSave = () => {
    setError(null);
    if (typeof body === 'string') return setError(body);
    save.mutate(body, {
      onSuccess: (saved) => {
        if (saved) setTexts(textsOf(saved));
        toast.success('Pressure-control settings saved');
      },
      onError: (e) => setError(pcErrorMessage(e, 'The settings could not be saved.')),
    });
  };

  return (
    <div className="min-h-full" data-testid="pc-settings-page">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-4">
        {canWrite && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || save.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}

        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-white">BOP certificate limits</h3>
            <p className="mt-1 text-sm text-fieldiq-text">
              These are your company&rsquo;s defaults, not a regulation. Each certificate keeps the limits in force when it was
              recorded, so a change here applies to certificates recorded after it.
            </p>
            {!settings.stored && (
              <p className="mt-1 text-sm text-fieldiq-muted" data-testid="pc-settings-defaults">
                Nothing has been saved yet, so these are the standard defaults.
              </p>
            )}
            {!canWrite && (
              <p className="mt-1 text-sm text-fieldiq-muted">Only an admin or office staff can change these.</p>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="mb-1.5 block text-xs font-medium text-fieldiq-text">{f.label}</span>
                <input
                  className={inputCls}
                  inputMode="numeric"
                  value={texts[f.key]}
                  disabled={!canWrite}
                  aria-label={f.label}
                  onChange={(e) => setTexts({ ...texts, [f.key]: e.target.value })}
                />
                <span className="mt-1 block text-xs text-fieldiq-muted">{f.hint}</span>
              </label>
            ))}
          </div>
          {error && (
            <p role="alert" className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
              {error}
            </p>
          )}
        </div>

        <PcClassRulesCard canWrite={canWrite} />
        <PcDrillSettingsCard canWrite={canWrite} />
        <PcTestDefaultsCard canWrite={canWrite} />
      </div>
    </div>
  );
}
